import React, { forwardRef } from 'react';
import moment from 'moment';
import { amountInWordsINR } from './amountInWords';
import defaultLogo from './assets/gravitysoft-logo.png';
import defaultSignature from './assets/gravitysoft-signature.png';
import './TaxInvoicePrintLayout.css';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? moment(d).format('DD-MMMM-YYYY') : '');

const contactLine = (seller) => [seller.address, seller.phone ? `Ph: ${seller.phone}` : null, seller.website, seller.email ? `Email: ${seller.email}` : null].filter(Boolean).join(', ');

/**
 * Pixel-accurate render of the paper tax invoice, styled after GravitySoft's
 * own printed template — the single source of truth for both the on-screen
 * preview/print and the PDF export (captured via html2canvas).
 */
const TaxInvoicePrintLayout = forwardRef(({ invoice, defaultSeller }, ref) => {
    const inv = invoice || {};
    // Invoices raised after the buyer/seller master split carry their own seller
    // snapshot (seller_name etc.) so the printed invoice never changes if the
    // seller master is edited later. Older invoices predate that snapshot, so
    // fall back to whichever seller is currently the default.
    const fallback = defaultSeller || {};
    const seller = inv.seller_name
        ? {
              company_name: inv.seller_name,
              address: inv.seller_address,
              gst_no: inv.seller_gst_no,
              phone: inv.seller_phone,
              email: inv.seller_email,
              website: inv.seller_website,
              bank_name: inv.seller_bank_name,
              account_no: inv.seller_account_no,
              ifsc_code: inv.seller_ifsc_code,
              bank_branch: inv.seller_bank_branch
          }
        : fallback;
    const items = Array.isArray(inv.items) ? inv.items : [];
    const isInterState = Number(inv.igst_amount) > 0;

    const grandTotal = Number(inv.grand_total || 0);
    const words = inv.amount_in_words || amountInWordsINR(grandTotal);
    const logoSrc = seller.logo_url || defaultLogo;
    const signatureSrc = seller.signature_url || defaultSignature;

    return (
        <div className="ti-page" ref={ref}>
            <div className="ti-sheet">
                <div className="ti-logo-row">
                    <img src={logoSrc} alt={seller.company_name || 'Company'} className="ti-logo" />
                </div>

                <div className="ti-title-row">
                    <span className="ti-title">TAX INVOICE</span>
                    <span className="ti-title-sub">(Original)</span>
                </div>

                <table className="ti-box">
                    <tbody>
                        <tr>
                            <td className="ti-cell ti-half">
                                <div className="ti-text ti-strong">{seller.company_name}</div>
                                <div className="ti-text">{seller.address}</div>
                                {seller.gst_no && <div className="ti-text">GSTNO : {seller.gst_no}</div>}
                            </td>
                            <td className="ti-cell ti-half ti-nopad">
                                <table className="ti-meta-grid">
                                    <tbody>
                                        <tr>
                                            <td className="ti-meta-label">Invoice No:</td>
                                            <td className="ti-meta-label">Dated:</td>
                                        </tr>
                                        <tr>
                                            <td className="ti-meta-val ti-strong">{inv.invoice_no}</td>
                                            <td className="ti-meta-val ti-strong">{fmtDate(inv.invoice_date)}</td>
                                        </tr>
                                        <tr>
                                            <td className="ti-meta-label">Mode/Terms of Payment</td>
                                            <td className="ti-meta-val">{inv.mode_of_payment || 'Other'}</td>
                                        </tr>
                                        <tr>
                                            <td className="ti-meta-label">Reference(s)</td>
                                            <td className="ti-meta-val">{inv.reference_no || ''}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                        <tr>
                            <td className="ti-cell ti-half">
                                <div className="ti-text">Buyer,</div>
                                <div className="ti-text ti-strong">{inv.buyer_name}</div>
                                <div className="ti-text">{inv.buyer_address}</div>
                                {inv.buyer_gst_no && <div className="ti-text">GST No : {inv.buyer_gst_no}</div>}
                            </td>
                            <td className="ti-cell ti-half">
                                <div className="ti-text">Terms Of delivery</div>
                                <div className="ti-text">{inv.terms_of_delivery || ''}</div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <table className="ti-items">
                    <thead>
                        <tr>
                            <th style={{ width: '7%' }}>Sl. No</th>
                            <th style={{ width: '43%' }}>Description</th>
                            <th style={{ width: '13%' }}>Qty</th>
                            <th style={{ width: '17%' }}>Rate</th>
                            <th style={{ width: '20%' }}>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, idx) => (
                            <tr key={item.item_id || idx}>
                                <td className="ti-center">{idx + 1}</td>
                                <td>
                                    {item.description}
                                    {item.sac_code && <span> (SAC-{item.sac_code})</span>}
                                </td>
                                <td className="ti-center">{Number(item.qty || 0).toLocaleString('en-IN')}</td>
                                <td className="ti-right">{fmt(item.rate)}</td>
                                <td className="ti-right ti-strong">{fmt(item.amount)}</td>
                            </tr>
                        ))}

                        {isInterState ? (
                            <tr>
                                <td className="ti-noborder" />
                                <td className="ti-right ti-noborder">IGST</td>
                                <td className="ti-center">{Number(inv.igst_percent || 0)}%</td>
                                <td className="ti-right">{fmt(inv.igst_amount)}</td>
                                <td className="ti-right ti-strong">{fmt(inv.igst_amount)}</td>
                            </tr>
                        ) : (
                            <>
                                <tr>
                                    <td rowSpan={2} className="ti-noborder" />
                                    <td className="ti-right ti-noborder">CGST</td>
                                    <td className="ti-center">{Number(inv.cgst_percent || 0)}%</td>
                                    <td className="ti-right">{fmt(inv.cgst_amount)}</td>
                                    <td rowSpan={2} className="ti-right ti-strong ti-tax-total">
                                        {fmt(Number(inv.cgst_amount || 0) + Number(inv.sgst_amount || 0))}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="ti-right ti-noborder">SGST</td>
                                    <td className="ti-center">{Number(inv.sgst_percent || 0)}%</td>
                                    <td className="ti-right">{fmt(inv.sgst_amount)}</td>
                                </tr>
                            </>
                        )}

                        <tr className="ti-grand-row">
                            <td colSpan={4} className="ti-right ti-strong">
                                Grand Total
                            </td>
                            <td className="ti-right ti-strong">{fmt(grandTotal)}</td>
                        </tr>
                    </tbody>
                </table>

                <div className="ti-words-box">
                    <div className="ti-words-row">
                        <span className="ti-strong">Balance Payable (in words):</span>
                        <span className="ti-eoe">E.&amp; O.E</span>
                    </div>
                    <div className="ti-text ti-strong">{words}</div>
                </div>

                <div className="ti-remarks">
                    <div className="ti-strong ti-italic">Remarks:</div>
                    <div className="ti-text ti-small ti-italic">{inv.remarks || 'Payment is to be made on presentation of Invoice or before the Due date mentioned on the Invoice.\nPayment is to be made by A/c Payee Cheque or Bank Transfer.'}</div>
                </div>

                <table className="ti-footer-table">
                    <tbody>
                        <tr>
                            <td className="ti-cell ti-half ti-noborder-outer">
                                {seller.bank_name && (
                                    <table className="ti-kv-grid">
                                        <tbody>
                                            <tr>
                                                <td className="ti-kv-label">Name</td>
                                                <td className="ti-kv-val">: {seller.company_name?.toUpperCase()}</td>
                                            </tr>
                                            <tr>
                                                <td className="ti-kv-label">A/C No</td>
                                                <td className="ti-kv-val">: {seller.account_no}</td>
                                            </tr>
                                            <tr>
                                                <td className="ti-kv-label">IFSC Code</td>
                                                <td className="ti-kv-val">: {seller.ifsc_code}</td>
                                            </tr>
                                            <tr>
                                                <td className="ti-kv-label">Bank</td>
                                                <td className="ti-kv-val">: {seller.bank_name}</td>
                                            </tr>
                                            <tr>
                                                <td className="ti-kv-label">Branch</td>
                                                <td className="ti-kv-val">: {seller.bank_branch}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                )}
                            </td>
                            <td className="ti-cell ti-half ti-noborder-outer ti-sign-cell">
                                <img src={signatureSrc} alt="Authorized Signature" className="ti-sign-img" />
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div className="ti-computer-generated">This is a Computer Generated Invoice</div>

                <div className="ti-bottombar">{contactLine(seller)}</div>
            </div>
        </div>
    );
});

export default TaxInvoicePrintLayout;
